// İSG uzmanı portalı — Taşeron oluştur/düzenle formu (Modal + RHF + Zod). api-contract
// §13.1: `POST /contractors` (create) / `PUT /contractors/{uuid}` (update). Alanlar: ad
// (zorunlu), yasal unvan, vergi no/dairesi, İSG-KATİP no, **tehlike sınıfı** (1/2/3 — Select),
// telefon, e-posta, adres, sözleşme başlangıç/bitiş (DateOnly, §4). **Durum** (Active/
// Suspended/Inactive) yalnız düzenlemede gösterilir (create'te varsayılan Active — istekte yok).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  type ContractorDto,
  Field,
  FormSection,
  type HazardClass,
  Input,
  Modal,
  Select,
  type SelectOption,
  Spinner,
  contractorsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { contractorsKeys } from '../../lib/contractorsKeys';

interface ContractorFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma. */
  contractor?: ContractorDto | null;
  onSuccess?: (contractor: ContractorDto) => void;
}

/** Boş/whitespace dizeyi `null`'a indirger (opsiyonel alanlar backend'e `null` gider). */
function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

export function ContractorFormModal({
  open,
  onOpenChange,
  contractor,
  onSuccess,
}: ContractorFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(contractor);

  const hazardOptions: SelectOption[] = useMemo(
    () => [
      { value: '1', label: t('hazard.1') },
      { value: '2', label: t('hazard.2') },
      { value: '3', label: t('hazard.3') },
    ],
    [t],
  );

  const statusOptions: SelectOption[] = useMemo(
    () => [
      { value: 'Active', label: t('contractors.status.active') },
      { value: 'Suspended', label: t('contractors.status.suspended') },
      { value: 'Inactive', label: t('contractors.status.inactive') },
    ],
    [t],
  );

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('contractors.validation.nameRequired')),
        legalName: z.string().trim().optional(),
        taxNumber: z.string().trim().optional(),
        taxOffice: z.string().trim().optional(),
        isgKatipNo: z.string().trim().optional(),
        hazardClass: z.enum(['1', '2', '3']),
        phone: z.string().trim().optional(),
        email: z.string().trim().optional(),
        address: z.string().trim().optional(),
        contractStart: z.string().optional(),
        contractEnd: z.string().optional(),
        status: z.enum(['Active', 'Suspended', 'Inactive']).optional(),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: contractor?.name ?? '',
      legalName: contractor?.legalName ?? '',
      taxNumber: contractor?.taxNumber ?? '',
      taxOffice: contractor?.taxOffice ?? '',
      isgKatipNo: contractor?.isgKatipNo ?? '',
      hazardClass: String(contractor?.hazardClass ?? 1) as '1' | '2' | '3',
      phone: contractor?.phone ?? '',
      email: contractor?.email ?? '',
      address: contractor?.address ?? '',
      contractStart: contractor?.contractStart ?? '',
      contractEnd: contractor?.contractEnd ?? '',
      status: contractor?.status ?? 'Active',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const base = {
        name: values.name.trim(),
        legalName: nullable(values.legalName),
        taxNumber: nullable(values.taxNumber),
        taxOffice: nullable(values.taxOffice),
        isgKatipNo: nullable(values.isgKatipNo),
        hazardClass: Number(values.hazardClass) as HazardClass,
        phone: nullable(values.phone),
        email: nullable(values.email),
        address: nullable(values.address),
        contractStart: nullable(values.contractStart),
        contractEnd: nullable(values.contractEnd),
      };
      return contractor
        ? contractorsApi.updateContractor(contractor.uuid, { ...base, status: values.status })
        : contractorsApi.createContractor(base);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: contractorsKeys.list() });
      if (contractor) {
        queryClient.invalidateQueries({ queryKey: contractorsKeys.detail(contractor.uuid) });
      }
      reset();
      onSuccess?.(saved);
      onOpenChange(false);
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('contractors.form.edit.title') : t('contractors.form.create.title')}
      description={t('contractors.form.description')}
      size="lg"
      closeLabel={t('contractors.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('contractors.common.cancel')}
          </Button>
          <Button type="submit" form="contractor-form" loading={mutation.isPending}>
            {mutation.isPending ? (
              <Spinner label={t('contractors.common.save')} />
            ) : isEdit ? (
              t('contractors.common.save')
            ) : (
              t('contractors.common.create')
            )}
          </Button>
        </>
      }
    >
      <form
        id="contractor-form"
        className="flex flex-col gap-8"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        {/* Firma kimliği */}
        <FormSection legend={t('contractors.form.section.identity')}>
          <Field
            htmlFor="contractor-name"
            label={t('contractors.form.field.name')}
            error={formState.errors.name?.message}
            required
          >
            <Input
              id="contractor-name"
              invalid={Boolean(formState.errors.name)}
              {...register('name')}
            />
          </Field>
          <Field htmlFor="contractor-legal" label={t('contractors.form.field.legalName')}>
            <Input id="contractor-legal" {...register('legalName')} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field htmlFor="contractor-tax" label={t('contractors.form.field.taxNumber')}>
              <Input id="contractor-tax" {...register('taxNumber')} />
            </Field>
            <Field htmlFor="contractor-taxoffice" label={t('contractors.form.field.taxOffice')}>
              <Input id="contractor-taxoffice" {...register('taxOffice')} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field htmlFor="contractor-katip" label={t('contractors.form.field.isgKatipNo')}>
              <Input id="contractor-katip" {...register('isgKatipNo')} />
            </Field>
            <Field
              htmlFor="contractor-hazard"
              label={t('contractors.form.field.hazardClass')}
              required
            >
              <Select id="contractor-hazard" options={hazardOptions} {...register('hazardClass')} />
            </Field>
          </div>
        </FormSection>

        {/* İletişim + sözleşme */}
        <FormSection legend={t('contractors.form.section.contact')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field htmlFor="contractor-phone" label={t('contractors.form.field.phone')}>
              <Input id="contractor-phone" type="tel" {...register('phone')} />
            </Field>
            <Field htmlFor="contractor-email" label={t('contractors.form.field.email')}>
              <Input id="contractor-email" type="email" {...register('email')} />
            </Field>
          </div>
          <Field htmlFor="contractor-address" label={t('contractors.form.field.address')}>
            <Input id="contractor-address" {...register('address')} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field htmlFor="contractor-start" label={t('contractors.form.field.contractStart')}>
              <Input id="contractor-start" type="date" {...register('contractStart')} />
            </Field>
            <Field htmlFor="contractor-end" label={t('contractors.form.field.contractEnd')}>
              <Input id="contractor-end" type="date" {...register('contractEnd')} />
            </Field>
          </div>
          {isEdit ? (
            <Field htmlFor="contractor-status" label={t('contractors.form.field.status')}>
              <Select id="contractor-status" options={statusOptions} {...register('status')} />
            </Field>
          ) : null}
        </FormSection>
      </form>
    </Modal>
  );
}
